import React, { useContext, useState, useReducer, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Auth from 'j-toker';
import capitalize from 'capitalize';

import { ApplicationUIContext, Table, MoneyInput } from '@yuhuinc/ui-library';
import { Form, Select, Space, Button, Divider, Alert, DatePicker, Input, Radio } from 'antd';

import { formatMoney } from 'helpers/Formatter';
import { getTranslatedString } from 'helpers/i18n';
import { isAfter, endOfDay } from 'date-fns';
import snakeCase from 'lodash/snakeCase';
import isEmpty from 'lodash/isEmpty';

import { apiGet } from 'helpers/ApiHelper';

const LEASE_ITEM_TYPE_FORM = 'lease_item_type_form';
const LEASE_ITEM_FORM = 'lease_item_form';

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px ${({ isMobile }) => (isMobile ? '16px' : '24px')};
`;

const StyledSpace = styled(Space)`
  .ant-space-item {
    flex: 1;
  }
`;

const StyledRadioGroup = styled(Radio.Group)`
  width: 100%;
`;

const StyledRadioButton = styled(Radio.Button)`
  width: 50%;
  text-align: center;
`;

const requiredField = {
  required: true,
  message: getTranslatedString('requiredField'),
};

const requiredAncillaryItemField = {
  required: true,
  message: getTranslatedString('landlordPortal.rentableItems.attributes.ancillaryItemId.required'),
};

const LEASE_ITEM_COLUMNS = [
  {
    title: getTranslatedString('landlordPortal.rentableItems.table.space'),
    dataIndex: 'asset_number',
  },
  {
    title: getTranslatedString('landlordPortal.rentableItems.table.monthly'),
    dataIndex: 'market_amount',
    render: (text) => formatMoney(text),
  },
  {
    title: getTranslatedString('landlordPortal.rentableItems.table.deposit'),
    dataIndex: 'market_deposit_amount',
    render: (text) => formatMoney(text),
  },
];

const LEASE_ITEM_TYPES_COLUMNS = [
  {
    title: getTranslatedString('landlordPortal.rentableItems.attributes.type'),
    dataIndex: 'subtype_name',
  },
];

const initialAncillaryItemDataState = {
  isFetchingAncillaryData: false,
  ancillaryItems: [],
  ancillaryItemSubtypes: [],
};

function reducer(state, [action, payload]) {
  switch (action) {
    case 'SET_FETCHING_ANCILLARY_DATA':
      return { isFetchingAncillaryData: true, ancillaryItems: [], ancillaryItemSubtypes: [] };
    case 'SET_ANCILLARY_ITEMS':
      return { ...state, isFetchingAncillaryData: false, ancillaryItems: payload };
    case 'SET_ANCILLARY_ITEM_SUBTYPES':
      return { ...state, isFetchingAncillaryData: false, ancillaryItemSubtypes: payload };
    default:
      return state;
  }
}

const AddLeaseItem = ({
  ancillaryItemTypes = [],
  initialValues = {},
  onAdd,
  closeDrawer,
  propertyId,
}) => {
  const { isMobile } = useContext(ApplicationUIContext);

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [form] = Form.useForm();
  const [hasError, setError] = useState(false);
  const values = Form.useWatch([], form);

  const [currentItemType, setItemType] = useState(undefined);
  const [leaseItemFormType, setLeaseItemFormType] = useState(LEASE_ITEM_FORM);
  const [columns, setColumns] = useState(LEASE_ITEM_TYPES_COLUMNS);

  const [{ isFetchingAncillaryData, ancillaryItems, ancillaryItemSubtypes }, dispatch] = useReducer(
    reducer,
    initialAncillaryItemDataState
  );

  const loadAncillaryItemSubtypes = useCallback(({ ancillaryItemTypeId }) => {
    const selectedAncillaryItemType = ancillaryItemTypes.find(
      (item_type) => item_type.id === ancillaryItemTypeId
    );
    setItemType(selectedAncillaryItemType);
    apiGet(
      Auth.getApiUrl() +
        `/landlords/application_management/ancillary_item_types/${ancillaryItemTypeId}/ancillary_item_subtypes`,
      { property_id: propertyId }
    ).then((res) => {
      let newColumns = [...LEASE_ITEM_TYPES_COLUMNS];
      dispatch(['SET_ANCILLARY_ITEM_SUBTYPES', res.ancillary_item_subtypes]);

      res.attribute_columns.forEach((attribute) => {
        newColumns.push({
          title: attribute,
          dataIndex: snakeCase(attribute),
          render: (value) => {
            if (value === undefined) return null;
            const valueString = value.toString();
            if (valueString === 'true') {
              return getTranslatedString('yes');
            } else if (valueString === 'false') {
              return getTranslatedString('no');
            }
            return capitalize(valueString);
          },
        });
      });

      setColumns(newColumns);
    });
  });

  const loadAncillaryItems = useCallback(({ ancillaryItemTypeId }) => {
    const selectedAncillaryItemType = ancillaryItemTypes.find(
      (item_type) => item_type.id === ancillaryItemTypeId
    );
    setItemType(selectedAncillaryItemType);
    apiGet(
      Auth.getApiUrl() +
        `/landlords/application_management/ancillary_item_types/${ancillaryItemTypeId}/ancillary_items`
    ).then((res) => {
      dispatch(['SET_ANCILLARY_ITEMS', res.ancillary_items]);
      setColumns(LEASE_ITEM_COLUMNS);
    });
  });

  useEffect(() => {
    if (isEmpty(initialValues)) return;
    dispatch(['SET_FETCHING_ANCILLARY_DATA']);

    if (initialValues.assign_item_type_later) {
      setLeaseItemFormType(LEASE_ITEM_TYPE_FORM);
      setSelectedRowKeys([initialValues.ancillary_item_subtype_id]);
      loadAncillaryItemSubtypes({ ancillaryItemTypeId: initialValues.ancillary_item_type_id });
    } else {
      setLeaseItemFormType(LEASE_ITEM_FORM);
      setSelectedRowKeys([initialValues.ancillary_item_id]);
      loadAncillaryItems({ ancillaryItemTypeId: initialValues.ancillary_item_type_id });
    }
  }, [initialValues]);

  const onLeaseItemFormTypeChange = (e) => {
    setLeaseItemFormType(e.target.value);
    setSelectedRowKeys([]);

    if (!isEmpty(currentItemType)) {
      onItemTypeChange(currentItemType.id, e.target.value);
    }
  };

  const onError = ({ errorFields }) => {
    if (errorFields.length > 1) {
      setError(true);
    }
  };

  const onFinish = (values) => {
    let leaseItem = { ...values, ancillary_item_type_id: currentItemType.id };
    if (!values.assign_item_type_later) {
      leaseItem = {
        ...leaseItem,
        payment_type: 'recurring',
        duration: 'monthly',
      };
    }
    onAdd(leaseItem);
    closeDrawer();
  };

  const onItemTypeChange = (value, formType = leaseItemFormType) => {
    dispatch(['SET_FETCHING_ANCILLARY_DATA']);

    if (formType === LEASE_ITEM_TYPE_FORM) {
      loadAncillaryItemSubtypes({ ancillaryItemTypeId: value });
    } else {
      loadAncillaryItems({ ancillaryItemTypeId: value });
    }
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys) => {
      setSelectedRowKeys(newSelectedRowKeys);
    },
    onSelect: (record, selected) => {
      if (leaseItemFormType === LEASE_ITEM_TYPE_FORM) {
        form.setFieldsValue({
          item_type: record.type_name,
          ancillary_item_subtype_id: record.id,
          asset_number: record.subtype_name,
          assign_item_type_later: true,
          // reset dollar amounts if ever was selected
          amount: 0,
          deposit_amount: 0,
          key_deposit_amount: 0,
        });
        return;
      }
      form.setFieldsValue({
        amount: record.market_amount,
        deposit_amount: record.market_deposit_amount,
        item_type: record.item_type,
        asset_number: record.asset_number,
        key_deposit_amount: record.market_key_deposit_amount,
        ancillary_item_id: record.id,
        assign_item_type_later: false,
      });
    },
    type: 'radio',
    fixed: true,
  };

  const disabledStartDate = (date) => {
    const startDate = endOfDay(new Date(date));

    const endDate = values?.end_date ? endOfDay(new Date(values.end_date)) : null;

    const isStartDateAfterEndDate = endDate ? isAfter(startDate, endDate) : false;

    return isStartDateAfterEndDate;
  };

  const disabledEndDate = (date) => {
    const startDate = values?.start_date ? endOfDay(new Date(values.start_date)) : null;

    const endDate = endOfDay(new Date(date));

    const isEndDateBeforeStartDate = startDate ? isAfter(startDate, endDate) : false;

    return isEndDateBeforeStartDate;
  };

  const rentableItemsTypes = ancillaryItemTypes.map((type) => ({
    value: type.id,
    label: type.type_name,
  }));

  return (
    <Form
      layout="vertical"
      size={isMobile && 'large'}
      form={form}
      name="add-rentable-item"
      scrollToFirstError
      onFinish={onFinish}
      onFinishFailed={onError}
      initialValues={initialValues}
    >
      <StyledContainer>
        {hasError && (
          <Alert message={getTranslatedString('errorsOnThisPage')} type="error" showIcon />
        )}

        <Form.Item name="asset_number" hidden>
          <Input />
        </Form.Item>
        <Form.Item name="key_deposit_amount" hidden>
          <Input />
        </Form.Item>
        <Form.Item name="assign_item_type_later" hidden>
          <Input />
        </Form.Item>

        <Form.Item
          name="item_type"
          label={getTranslatedString('landlordPortal.rentableItems.attributes.type')}
          rules={[requiredField]}
        >
          <Select onChange={(value) => onItemTypeChange(value)} options={rentableItemsTypes} />
        </Form.Item>

        <StyledRadioGroup value={leaseItemFormType} onChange={onLeaseItemFormTypeChange}>
          <StyledRadioButton value={LEASE_ITEM_FORM}>
            {getTranslatedString('landlordPortal.rentableItems.assignItemNow')}
          </StyledRadioButton>
          <StyledRadioButton value={LEASE_ITEM_TYPE_FORM}>
            {getTranslatedString('landlordPortal.rentableItems.assignItemLater')}
          </StyledRadioButton>
        </StyledRadioGroup>

        <div>{getTranslatedString('landlordPortal.rentableItems.selectARentableItem')}</div>

        <Form.Item
          name={
            leaseItemFormType === LEASE_ITEM_FORM
              ? 'ancillary_item_id'
              : 'ancillary_item_subtype_id'
          }
          rules={[requiredAncillaryItemField]}
        >
          <Table
            dataSource={
              leaseItemFormType === LEASE_ITEM_TYPE_FORM ? ancillaryItemSubtypes : ancillaryItems
            }
            columns={columns}
            hideActionAndSearch
            size={isMobile ? 'middle' : 'small'}
            rowSelection={rowSelection}
            pagination={{ pageSize: 10, showSizeChanger: false }}
            scroll={{ x: isMobile }}
            rowKey={(record) => record.id}
            loading={isFetchingAncillaryData}
          />
        </Form.Item>
        {leaseItemFormType === LEASE_ITEM_FORM && (
          <>
            <StyledSpace direction={isMobile ? 'vertical' : 'horizontal'}>
              <Form.Item
                name="start_date"
                label={getTranslatedString('landlordPortal.rentableItems.attributes.startDate')}
                rules={[requiredField]}
              >
                <DatePicker style={{ width: '100%' }} disabledDate={disabledStartDate} />
              </Form.Item>

              <Form.Item
                name="end_date"
                label={getTranslatedString('landlordPortal.rentableItems.attributes.endDate')}
                rules={[requiredField]}
              >
                <DatePicker style={{ width: '100%' }} disabledDate={disabledEndDate} />
              </Form.Item>
            </StyledSpace>

            <StyledSpace direction={isMobile ? 'vertical' : 'horizontal'}>
              <Form.Item
                name="amount"
                label={getTranslatedString('landlordPortal.rentableItems.attributes.rent')}
                rules={[requiredField]}
              >
                <MoneyInput />
              </Form.Item>

              <Form.Item
                name="deposit_amount"
                label={getTranslatedString('landlordPortal.rentableItems.attributes.deposit')}
                rules={[requiredField]}
                shouldUpdate
              >
                <MoneyInput />
              </Form.Item>
            </StyledSpace>
          </>
        )}

        <Divider />

        <Space>
          <Button type="primary" htmlType="submit">
            {initialValues.id ? getTranslatedString('save') : getTranslatedString('add')}
          </Button>

          <Button htmlType="button" onClick={closeDrawer}>
            {getTranslatedString('cancel')}
          </Button>
        </Space>
      </StyledContainer>
    </Form>
  );
};

AddLeaseItem.propTypes = {
  closeDrawer: PropTypes.func.isRequired,
  initialValues: PropTypes.object,
  ancillaryItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      market_key_deposit_amount: PropTypes.number.isRequired,
      item_type: PropTypes.string.isRequired,
      asset_number: PropTypes.string.isRequired,
      market_amount: PropTypes.number.isRequired,
      market_deposit_amount: PropTypes.number.isRequired,
    })
  ),
};

AddLeaseItem.defaultProps = {
  ancillaryItems: [],
};

export default AddLeaseItem;
